'use client';
import { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <Link href="/">
            <AppImage
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={200}
              height={75}
              className="mx-auto w-[180px] h-auto mb-8"
            />
          </Link>
        </div>

        {sent ? (
          /* Success State */
          <div className="bg-surface-1 border border-border p-8 text-center">
            <div className="w-16 h-16 bg-success/10 border border-success/20 flex items-center justify-center mx-auto mb-6">
              <Icon name="EnvelopeIcon" size={28} className="text-success" />
            </div>
            <h2 className="text-h4 mb-3">Check Your Email</h2>
            <p className="text-body-sm text-muted-foreground mb-2">
              We sent a password reset link to:
            </p>
            <p className="text-body-sm text-foreground font-medium mb-6">{email}</p>
            <p className="text-label text-muted-foreground mb-8">
              Click the link in the email to reset your password. The link expires in 1 hour.
            </p>
            <Link
              href="/login"
              className="inline-flex items-center gap-2 text-label uppercase tracking-widest text-primary hover:text-primary-hover transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Back to Sign In
            </Link>
          </div>
        ) : (
          /* Form State */
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 mb-6">
                <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                <span className="text-micro uppercase tracking-widest text-primary">Password Reset</span>
              </div>
              <h1 className="text-h3 mb-2">Forgot Password?</h1>
              <p className="text-body-sm text-muted-foreground">
                Enter your email and we will send you a reset link.
              </p>
            </div>

            <div className="bg-surface-1 border border-border p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-label uppercase tracking-widest text-muted-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@company.com"
                    className="w-full bg-surface-2 border border-border px-4 py-3 text-body-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30">
                    <Icon name="ExclamationCircleIcon" size={18} className="text-error mt-0.5 shrink-0" />
                    <p className="text-label text-error">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-hover text-white py-4 text-label uppercase tracking-widest transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </div>

            <div className="mt-8 text-center">
              <Link
                href="/login"
                className="text-micro uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
